import { useCallback } from 'react'
import { flushSync } from 'react-dom'
import { useNavigate } from '../components/nav'
import type { NavigateOptions } from 'react-router-dom'

// React Router's built-in view-transition helpers (`<Link viewTransition>`,
// `navigate(to, { viewTransition: true })`, `useViewTransitionState`) only work under a *data*
// router (createBrowserRouter). This app mounts a plain `<BrowserRouter>`, so those APIs throw
// ("must be used within a data router"). This drives the native View Transitions API directly
// instead, which works with any router: it wraps the navigation in `document.startViewTransition`
// and uses `flushSync` so the route swap (and the new page's DOM) is captured within the transition.

type StartViewTransition = (callback: () => void) => unknown

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Returns a navigate function that animates the route change with a native view transition when
 * supported. `beforeTransition` runs synchronously *before* the old-page snapshot is captured —
 * use it to tag the element that should "hero" (e.g. set `view-transition-name`).
 */
export function useViewTransitionNavigate() {
  const navigate = useNavigate()
  return useCallback((to: string, beforeTransition?: () => void, options?: NavigateOptions) => {
    const doc = document as Document & { startViewTransition?: StartViewTransition }
    if (typeof doc.startViewTransition !== 'function' || prefersReducedMotion()) {
      navigate(to, options)
      return
    }
    beforeTransition?.()
    doc.startViewTransition(() => {
      flushSync(() => navigate(to, options))
    })
  }, [navigate])
}

/** True for a plain left-click with no modifier keys — i.e. a navigation we should intercept.
 *  Modifier/middle clicks fall through so "open in new tab" etc. keep working on the anchor. */
export function isPlainLeftClick(e: React.MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey
}
